import { get, omitBy } from "lodash/fp";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Container,
  Content,
  FlexboxGrid,
  Footer,
  Header,
  Pagination,
  Table,
  Input,
  InputGroup,
} from "rsuite";
import SearchIcon from "@rsuite/icons/Search";
import CloseIcon from "@rsuite/icons/Close";
import { ChartServices } from "../../../core/services/ChartServices";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { AlertModal } from "../../components/Modals/AlertModal/AlertModal";
import { ChartFields, ChartListParams, Permission } from "./ChartDataTypes";
import "./Charts.css";
import ChartFilters from "./ChartFilters";
import { displayChartCompany, isEmptyVal } from "../../../utils/helpers";
import ImageCellFormatter from "../../components/Elements/ImageCellFormatter";
import CustomCell from "../../components/Elements/CustomCell";
import CustomHeaderCell from "../../components/Elements/CustomHeaderCell";
import ChartSingleAction from "./ChartSingleAction";
import { ContentRoutes } from "../../../navigation/NavConstant";
import { ToastContainer } from "react-toastify";
import ImageModal from "../../components/Modals/ImageModal/ImageModal";
import {
  roleTypeArr,
  statusArr,
  messageTypeArr,
  appConstObj,
  chartCatTypeObj,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import { displayField } from "../../../utils/helpers";
import ChartInfoCell from "../../components/Elements/ChartInfoCell";
import ChartSongs from "./ChartSongs";

interface ImageModal {
  openModal: () => void;
  removePic: () => void;
  hideDelete: () => void;
}

function FilteredCharts({
  filterParams,
  withHeader = true,
  enableSongBulkAction = [],
  enableChartBulkAction = [],
  ...props
}: {
  filterParams: any;
  withHeader?: boolean;
  enableSongBulkAction?: string[];
  enableChartBulkAction?: string[];
}) {
  const { t } = useTranslation();
  const { Cell, Column } = Table;
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(appConstObj.record_per_page);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<ChartFields[]>([]);
  const [sort_by, setSortBy] = useState<any>();
  const [sort_order, setSortOrder] = useState<any>();
  const company = useSelector((state: any) => state.company);
  const modalRef = useRef<ImageModal>(null);
  const [selectedChart, setSelectedChart] = useState<any>({});
  const [openPictureDelModal, setOpenPictureDelModal] = useState(false);
  const [smartSearchInput, setSmartSearch] = useState<string>("");
  const loggedInUserDetail = JSON.parse(
    localStorage.getItem("loggedInUserDetail") || "{}"
  );

  const initSearchFormData = {
    name: filterParams?.name ? filterParams?.name : "",
    company: filterParams?.company ? filterParams?.company : "",
    store_group: filterParams?.store_group ? filterParams?.store_group : "",
    event: filterParams?.event ? filterParams?.event : "",
    license: filterParams?.license ? filterParams?.license : "",
    company_type: filterParams?.company_type ? filterParams?.company_type : "",
    for: filterParams?.for ? filterParams?.for : "",
    for_all_company: filterParams?.for_all_company
      ? filterParams?.for_all_company
      : "",
    type: filterParams?.type ? filterParams?.type : "",
    availability: filterParams?.availability ? filterParams?.availability : "",
    start_date: filterParams?.start_date ? filterParams?.start_date : "",
    end_date: filterParams?.end_date ? filterParams?.end_date : "",
    status: filterParams?.status ? filterParams?.status : "",
    smart_search: filterParams?.smart_search ? filterParams?.smart_search : "",
  };

  const [formData, setFormData] = useState<ChartListParams>(initSearchFormData);
  const [showCreate, setShowCreate] = useState<boolean>();
  const [chartFetched, setChartFetched] = useState<boolean>(false);
  const navigate = useNavigate();
  const [checkedKeys, setCheckedKeys] = React.useState<number[]>([]);
  const [failedImages, setFailedImages] = useState<any>([]);
  const publishPermission = UtilityServices.hasPermission("publish-chart");
  const updatePermission = UtilityServices.hasPermission("update-chart");
  const deletePermission = UtilityServices.hasPermission("delete-chart");
  const permissionObj: Permission = {
    publish: enableChartBulkAction.includes("publish")
      ? publishPermission
      : false,
    update: enableChartBulkAction.includes("update") ? updatePermission : false,
    delete: enableChartBulkAction.includes("delete") ? deletePermission : false,
  };

  function selectChart(resData: any, selectChartId: number = 0) {
    setSelectedChart({});
    if (resData.length > 0) {
      if (selectChartId == 0) {
        setSelectedChart(resData[0]);
      } else {
        let chartToSelect = data
          .filter((item) => item.id == selectChartId)
          .shift();
        if (chartToSelect) {
          setSelectedChart(chartToSelect);
        } else {
          setSelectedChart(resData[0]);
        }
      }
    }
  }

  async function getCharts(params: ChartListParams, selectChartId: number = 0) {
    try {
      setLoading(true);
      setChartFetched(false);
      params = { ...params, ...filterParams };
      const res = await ChartServices.getCharts(params);
      const resData = get("data.data", res);
      setData(resData);
      setTotal(get("data.total", res));
      selectChart(resData, selectChartId);
      setCheckedKeys([]);
      setLoading(false);
      setChartFetched(true);
    } catch (error) {
      setLoading(false);
    }
  }

  async function init() {
    try {
      setShowCreate(UtilityServices.hasPermission("create-chart"));
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    init();
  }, []);

  async function deleteChart(chart: any) {
    try {
      let res = await ChartServices.deleteChart(chart?.id);
      setPage(1);
      getCharts(
        getSearchParams({
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  function fetchChartSongs(chart: any) {
    setSelectedChart(chart);
  }

  async function publishChart(chart: any) {
    try {
      let status =
        chart?.status == statusArr.inactive
          ? statusArr.active
          : statusArr.inactive;
      let res = await ChartServices.publishChart(chart?.id, {
        status,
      });
      setPage(1);
      getCharts(
        getSearchParams({
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function deletePic() {
    try {
      setOpenPictureDelModal(false);

      let res = await ChartServices.deleteChartLogo(selectedChart.id);
      setPage(1);
      getCharts(
        getSearchParams({
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  function getFailedImages(val: any) {
    setFailedImages(val);
  }

  function chartPicModal(value: any) {
    if (
      !failedImages.includes(value.chart_image_url) &&
      value.chart_image_url != null &&
      value.chart_image_url !== ""
    ) {
      setSelectedChart(value);
      modalRef.current?.openModal();
    }
  }

  function getSearchParams(data?: any) {
    let paramsData = {
      ...formData,
      ...{ smart_search: smartSearchInput },
      ...{ sort_by, sort_order },
      ...{ company: getCompanyId() },
      ...data,
    };

    let params: ChartListParams = omitBy(isEmptyVal, paramsData);
    if (data?.page) {
      params.page = data?.page;
    }

    return params;
  }

  function getCompanyId() {
    let compId = null;
    if (UtilityServices.getLoggedInUser("user_type") === roleTypeArr.client) {
      compId = UtilityServices.getLoggedInUser("company_id");
    } else if (company?.id) {
      compId = company.id;
    }
    return compId;
  }

  function onPageChange(page: number) {
    setPage(page);
    getCharts(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  }

  function onSortChange(dataKey: any, sortType: any) {
    setPage(1);
    setSortBy(dataKey);
    setSortOrder(sortType);
    getCharts(
      getSearchParams({
        page: 1,
        sort_by: dataKey,
        sort_order: sortType,
        rows: rows,
      })
    );
  }

  function submitSearchAfterSongChange(prevChartId: any) {
    setPage(page);
    getCharts(getSearchParams(formData), prevChartId);
  }

  const filterCharts = (e: any) => {
    if (e.key === "Enter" || e.type === "click") {
      getCharts(
        getSearchParams({
          smart_search: smartSearchInput,
          rows: rows,
        })
      );
    }
  };

  const clearSmartSearch = () => {
    setSmartSearch("");
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getCharts(
      getSearchParams({
        smart_search: "",
        page: 1,
        sort_by: null,
        sort_order: null,
        rows: appConstObj.record_per_page,
      })
    );
  };

  const handleChangeRows = (rows: any) => {
    setRows(rows);
    setPage(1);
    getCharts(
      getSearchParams({
        page: 1,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  };

  const [showColumns, setShowColumns] = useState([
    { fieldName: t("IMAGE") },
    { fieldName: t("NAME") },
  ]);

  useEffect(() => {
    getCharts(
      getSearchParams({
        page: 1,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
    setShowColumns([{ fieldName: t("IMAGE") }, { fieldName: t("NAME") }]);
  }, [company]);

  return (
    <div className="charts customers">
      <Container className="container">
        {withHeader && (
          <Header className="header">
            <FlexboxGrid justify="space-between">
              <FlexboxGrid.Item colspan={8}>
                <h4>{t("CHARTS")}</h4>
              </FlexboxGrid.Item>
              {showCreate && (
                <FlexboxGrid.Item colspan={4} style={{ textAlign: "end" }}>
                  <Button
                    appearance="primary"
                    onClick={() => navigate(ContentRoutes.CREATE_CHART)}
                  >
                    {t("CREATE")}
                  </Button>
                </FlexboxGrid.Item>
              )}
            </FlexboxGrid>
          </Header>
        )}

        <Content>
          <div style={{ display: "flex", width: "100%" }}>
            <div
              className={total > 0 ? "usr-tbl-border" : "no-record-table"}
              style={{ width: "40%" }}
            >
              <div className="row">
                <div className="col-md-12">
                  <InputGroup>
                    <Input
                      className="form-control"
                      placeholder={t("SMART_SEARCH_BY", {
                        entity:
                          t("CHART").toLowerCase() +
                          " " +
                          t("NAME").toLowerCase() +
                          ", " +
                          t("TYPE").toLowerCase(),
                      })}
                      value={smartSearchInput}
                      onChange={(val: any) => setSmartSearch(val)}
                      onKeyDown={(e: any) => filterCharts(e)}
                    />
                    <InputGroup.Button
                      title={t("SEARCH")}
                      onClick={(e: any) => filterCharts(e)}
                    >
                      <SearchIcon />
                    </InputGroup.Button>
                    <InputGroup.Button
                      title={t("CLEAR")}
                      onClick={() => clearSmartSearch()}
                    >
                      <CloseIcon />
                    </InputGroup.Button>
                  </InputGroup>
                </div>
              </div>

              <div>
                <Table
                  height={400}
                  fillHeight={true}
                  data={data}
                  loading={loading}
                  onSortColumn={onSortChange}
                  sortColumn={sort_by}
                  bordered={true}
                  cellBordered
                  sortType={sort_order}
                  rowHeight={80}
                  hover={true}
                >
                  {showColumns?.length &&
                    showColumns.map((field: any, index: number) => {
                      return (
                        <>
                          {field.fieldName === t("IMAGE") ? (
                            <Column width={80} align="center" fixed key={index}>
                              <CustomHeaderCell>{t("IMAGE")}</CustomHeaderCell>
                              <ImageCellFormatter
                                dataKey="chart_image_url"
                                rowData={(rowData: any) => rowData}
                                dummyImage={UtilityServices.getLoggedInUser(
                                  "chart_default_image"
                                )}
                                onFailedImages={getFailedImages}
                                openPictureModal={chartPicModal}
                              />
                            </Column>
                          ) : null}

                          {field.fieldName === t("NAME") ? (
                            <Column
                              width={220}
                              sortable
                              resizable
                              flexGrow={1}
                              fixed
                              key={index}
                            >
                              <CustomHeaderCell>{t("NAME")}</CustomHeaderCell>
                              <ChartInfoCell
                                dataKey="chart_title"
                                rowData={(rowData: any) => rowData}
                                onClick={fetchChartSongs}
                              />
                            </Column>
                          ) : null}

                          {field.fieldName === t("LICENSE") ? (
                            <Column width={120} resizable key={index}>
                              <CustomHeaderCell>
                                {t("LICENSE")}
                              </CustomHeaderCell>
                              <CustomCell dataKey="license_name" />
                            </Column>
                          ) : null}

                          {field.fieldName === t("TYPE") ? (
                            <Column width={120} resizable key={index}>
                              <CustomHeaderCell>{t("TYPE")}</CustomHeaderCell>
                              <CustomCell dataKey="chart_type" />
                            </Column>
                          ) : null}

                          {field.fieldName === t("COMPANY") ? (
                            <Column width={120} resizable key={index}>
                              <CustomHeaderCell>
                                {t("COMPANY")}
                              </CustomHeaderCell>
                              <CustomCell dataKey="company_name">
                                {(rowData: any) => (
                                  <span>{displayChartCompany(rowData)}</span>
                                )}
                              </CustomCell>
                            </Column>
                          ) : null}

                          {field.fieldName === t("SONGS") ? (
                            <Column width={50} sortable key={index}>
                              <CustomHeaderCell>{t("SONGS")}</CustomHeaderCell>
                              <CustomCell dataKey="total_songs" />
                            </Column>
                          ) : null}
                        </>
                      );
                    })}

                  <Column width={60} fixed="right">
                    <CustomHeaderCell>{t("ACTION")}</CustomHeaderCell>
                    <Cell>
                      {(rowData) => (
                        <ChartSingleAction
                          chartData={rowData}
                          onDeleteChart={deleteChart}
                          onPublishChart={publishChart}
                          permission={permissionObj}
                        />
                      )}
                    </Cell>
                  </Column>
                </Table>
              </div>
              <Footer className="footer">
                <Pagination
                  prev
                  next
                  first
                  last
                  ellipsis
                  boundaryLinks
                  maxButtons={5}
                  size="xs"
                  layout={["total", "-", "|", "pager"]}
                  total={total}
                  limitOptions={appConstObj.limit_options}
                  limit={rows}
                  activePage={page}
                  onChangePage={onPageChange}
                  onChangeLimit={handleChangeRows}
                />
              </Footer>
            </div>
            <div style={{ width: "60%" }}>
              {chartFetched && (
                <ChartSongs
                  chartId={selectedChart?.id}
                  onDeleteSongs={submitSearchAfterSongChange}
                  chartData={selectedChart}
                  enableBulkAction={enableSongBulkAction}
                />
              )}
            </div>

            <ToastContainer />
          </div>
        </Content>
      </Container>
      <ImageModal
        ref={modalRef}
        modalTitle={t("CHART") + " " + t("IMAGE")}
        heading={t("CHART") + " " + t("IMAGE")}
        body={
          <img
            src={selectedChart.chart_image_url}
            className="main-profile-pic"
            alt={t("CHART") + " " + t("IMAGE")}
          />
        }
        removePic={() => setOpenPictureDelModal(true)}
      />
      <AlertModal
        open={openPictureDelModal}
        handleClose={(e: any) => {
          if (e == true) {
            deletePic();
          } else {
            setOpenPictureDelModal(false);
            setSelectedChart({});
          }
        }}
        displayText={t("CONFIRM_ACTION", {
          action: t("DELETE").toLowerCase(),
          entity: t("CHART").toLowerCase() + " " + t("IMAGE").toLowerCase(),
        })}
        modalTitle={t("CONFIRM_ACTION_TITLE", {
          action: t("DELETE").toLowerCase(),
        })}
      />
    </div>
  );
}

export default FilteredCharts;
